import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Phone } from 'lucide-react';
import { PHONE_NUMBER, PHONE_NUMBER_DISPLAY, trackPhoneClick } from '../utils/tracking';

const XIcon = () => (
  <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const TikTokIcon = () => (
  <svg viewBox="0 0 448 512" className="w-[18px] h-[18px]" fill="currentColor">
    <path d="M448 209.9a210.1 210.1 0 01-122.8-39.3v178.8A162.6 162.6 0 11185 188.3v89.9a74.6 74.6 0 1052.2 71.2V0h88a121 121 0 00122.8 121.3z"/>
  </svg>
);

const ThreadsIcon = () => (
  <svg viewBox="0 0 192 192" className="w-[18px] h-[18px]" fill="currentColor">
    <path d="M141.537 88.988a66.667 66.667 0 00-2.518-1.143c-1.482-27.307-16.403-42.94-41.457-43.1h-.34c-14.986 0-27.449 6.396-35.12 18.036l13.779 9.452c5.73-8.695 14.724-10.548 21.348-10.548h.229c8.249.053 14.474 2.452 18.503 7.129 2.932 3.405 4.893 8.111 5.864 14.05a78.93 78.93 0 00-21.484-2.448c-24.4 0-40.083 13.3-39.526 33.562.28 10.18 5.368 18.938 14.321 24.665 7.572 4.842 17.327 7.266 27.453 6.823 13.35-.584 23.789-5.57 31.049-14.836 5.498-7.027 9.028-16.082 10.627-27.282 6.357 3.833 11.091 8.89 13.75 15.14 4.528 10.645 4.793 28.127-9.926 42.85-12.908 12.908-28.437 18.49-51.673 18.67-25.77-.197-45.29-8.452-58.06-24.527C26.539 141.04 19.636 117.96 19.42 91.006c.216-26.954 7.119-50.034 20.507-64.982C52.698 9.944 72.217 1.69 97.987 1.493c25.957.197 45.68 8.47 58.672 24.589 6.376 7.918 11.138 17.725 14.183 29.086l15.848-4.248c-3.576-13.326-9.253-25.017-16.938-34.57C154.433 -1.674 130.703-11.89 97.98-12.112h-.067c-32.544.221-58.477 10.437-74.02 28.487C8.94 33.966.767 60.548.5 91.006v.012c.267 30.458 8.44 57.04 23.394 74.631C39.487 183.673 65.42 193.89 97.913 194.111h.067c27.593-.206 47.237-7.588 63.486-23.838 22.099-22.099 20.748-49.585 13.637-66.282-5.098-11.982-14.686-21.564-27.566-27.003zM99.684 130.876c-11.195.49-22.824-4.397-23.343-15.3-.388-8.137 5.758-17.2 24.6-17.2a63.25 63.25 0 0117.635 2.468c-2.01 19.792-10.14 29.537-18.892 30.032z"/>
  </svg>
);

const LinkedInIcon = () => (
  <svg viewBox="0 0 448 512" className="w-[18px] h-[18px]" fill="currentColor">
    <path d="M100.28 448H7.4V148.9h92.88zM53.79 108.1C24.09 108.1 0 83.5 0 53.8a53.79 53.79 0 01107.58 0c0 29.7-24.1 54.3-53.79 54.3zM447.9 448h-92.68V302.4c0-34.7-.7-79.2-48.29-79.2-48.29 0-55.69 37.7-55.69 76.7V448h-92.78V148.9h89.08v40.8h1.3c12.4-23.5 42.69-48.3 87.83-48.3 93.97 0 111.3 61.9 111.3 142.3V448z"/>
  </svg>
);

const socialLinks = [
  { icon: Facebook, href: 'https://facebook.com/montreon.ro/', label: 'Facebook' },
  { icon: Instagram, href: 'https://instagram.com/montreon.ro/', label: 'Instagram' },
  { icon: XIcon, href: 'https://x.com/montreonromania', label: 'X', isCustom: true },
  { icon: TikTokIcon, href: 'https://www.tiktok.com/@montreon.ro', label: 'TikTok', isCustom: true },
  { icon: ThreadsIcon, href: 'https://www.threads.com/@montreon.ro', label: 'Threads', isCustom: true },
  { icon: LinkedInIcon, href: 'https://www.linkedin.com/company/122504307/admin/page-posts/published/', label: 'LinkedIn', isCustom: true },
];

const Footer = ({ t, onNavigate, language }) => {
  const isRo = language === 'ro';

  const handlePhoneClick = () => {
    trackPhoneClick();
    window.location.href = `tel:${PHONE_NUMBER}`;
  };

  return (
    <footer className="bg-[#0D1B3E] py-16 sm:py-20" data-testid="footer">
      <div className="container mx-auto px-4 sm:px-6 lg:px-12">

        {/* Social Media Icons - Top Center */}
        <div className="flex items-center justify-center gap-3 mb-14" data-testid="footer-social-links">
          {socialLinks.map((s, i) => (
            <a
              key={i}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-[#10B981] transition-colors text-white"
              aria-label={s.label}
              data-testid={`footer-social-${s.label.toLowerCase()}`}
            >
              {s.isCustom ? <s.icon /> : <s.icon size={18} />}
            </a>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-12 mb-12 md:mb-16">
          {/* Logo & Tagline */}
          <div>
            <h3 className="font-display text-2xl font-bold text-white mb-4">
              MONTRÉON
            </h3>
            <p className="font-body text-white/60 text-sm leading-relaxed max-w-xs">
              {isRo
                ? 'Servicii complete de mobilitate — Închirieri auto, transfer aeroport și tururi turistice în Brașov.'
                : 'Complete mobility services — Car rentals, airport transfers and touristic tours in Brașov.'}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-body text-xs font-semibold text-white/40 tracking-[0.15em] uppercase mb-6">
              {isRo ? 'Navigare' : 'Navigation'}
            </h4>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/rent-a-car"
                  data-testid="footer-link-rentals"
                  className="font-body text-white/70 hover:text-[#10B981] transition-colors text-sm"
                >
                  {isRo ? 'Închirieri Auto' : 'Car Rentals'}
                </Link>
              </li>
              <li>
                <Link
                  to="/transfers"
                  className="font-body text-white/70 hover:text-[#10B981] transition-colors text-sm"
                >
                  {isRo ? 'Transferuri' : 'Transfers'}
                </Link>
              </li>
              <li>
                <Link
                  to="/tours"
                  className="font-body text-white/70 hover:text-[#10B981] transition-colors text-sm"
                >
                  {isRo ? 'Tururi Turistice' : 'Tours'}
                </Link>
              </li>
              <li>
                <a
                  href="/contact"
                  className="font-body text-white/70 hover:text-[#10B981] transition-colors text-sm"
                >
                  Contact
                </a>
              </li>
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="font-body text-xs font-semibold text-white/40 tracking-[0.15em] uppercase mb-6">
              Legal
            </h4>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/termeni-si-conditii"
                  className="font-body text-white/70 hover:text-[#10B981] transition-colors text-sm"
                >
                  {isRo ? 'Termeni și Condiții' : 'Terms & Conditions'}
                </Link>
              </li>
              <li>
                <Link
                  to="/politica-de-confidentialitate"
                  className="font-body text-white/70 hover:text-[#10B981] transition-colors text-sm"
                >
                  {isRo ? 'Politica de Confidențialitate' : 'Privacy Policy (GDPR)'}
                </Link>
              </li>
              <li>
                <Link
                  to="/politica-de-cookie"
                  className="font-body text-white/70 hover:text-[#10B981] transition-colors text-sm"
                >
                  {isRo ? 'Politica de Cookie-uri' : 'Cookie Policy'}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-body text-xs font-semibold text-white/40 tracking-[0.15em] uppercase mb-6">
              Contact
            </h4>
            <ul className="space-y-3">
              <li className="font-body text-white/70 text-sm">
                Str. Avram Iancu nr. 56, ap. 2, Brașov, 500075, România
              </li>
              <li>
                <button
                  onClick={handlePhoneClick}
                  data-testid="footer-phone-link"
                  className="font-body text-white/70 hover:text-[#10B981] transition-colors text-sm flex items-center gap-2"
                >
                  <Phone size={14} />
                  {PHONE_NUMBER_DISPLAY}
                </button>
              </li>
              <li>
                <a
                  href="mailto:contact@montreon.ro"
                  className="font-body text-white/70 hover:text-[#10B981] transition-colors text-sm"
                >
                  contact@montreon.ro
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Copyright */}
        <div className="pt-8 border-t border-white/10">
          <p className="font-body text-white/40 text-xs tracking-wide">
            © {new Date().getFullYear()} Montréon. {t.footer.rights}
          </p>
          <p className="font-body text-white/40 text-xs tracking-wide mt-2" data-testid="footer-company-details">
            {isRo ? 'Operat de' : 'Operated by'} Quantive Endeavors SRL · CUI 54042200 · Str. Avram Iancu nr. 56, ap. 2, Brașov
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
